import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/apiClient";
import { notifications } from "@mantine/notifications";
import { FORM_MUTATION_META } from "@/utils/formErrorHandler";
import type {
  Incident,
  IncidentInsert,
  IncidentUpdate,
} from "@/types/incident";
export type {
  Incident,
  IncidentInsert,
  IncidentUpdate,
} from "@/types/incident";
import { useFacilityContext } from "./useFacilityContext";
// SCRUM-231: an incident shows on the resident activity timeline but completes
// no derived task, so these paths reach the timeline directly rather than
// invalidating three task surfaces they have no business touching.
import { invalidateResidentTimeline } from "./taskSurfaces";

/** Server-side paginated incident list response (matches useCases's shape). */
export interface IncidentsPage {
  incidents: Incident[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Fetch incidents (optionally filtered by facility, status, severity,
 * search, and page), server-side paginated.
 *
 * **Facility Context Integration:**
 * - If `filters.facility_id` is provided, uses that value
 * - Otherwise, falls back to current facility from context
 * - This ensures shift-based staff see their facility's incidents automatically
 *
 * Was fully unbounded (fetched the entire facility-scoped incident history
 * on every call, paginated client-side) until the backend added real
 * `limit`/`offset`/`search` support — see backend/src/app/api/incidents/route.ts.
 */
export function useIncidents(
  filters?: {
    facility_id?: string;
    status?: string | string[];
    severity?: string;
    /**
     * Reporter origin: "system" (automation-generated) or "staff" (human-filed).
     * Omit for both. Lets the list separate auto-incidents from staff reports.
     */
    source?: "system" | "staff";
    /** Free-text match across description, type, and resident name (server-side). */
    search?: string;
    /** 1-indexed page number. Combined with `limit` to compute the offset sent to the server. */
    page?: number;
    /** Page size. Defaults to the backend's own default (50) when omitted. */
    limit?: number;
  },
  options?: { enabled?: boolean },
) {
  const { currentFacilityId } = useFacilityContext();

  // Use provided facility_id or fallback to context
  const effectiveFacilityId = filters?.facility_id ?? currentFacilityId;

  // Rebuild filters with effective facility_id
  const effectiveFilters = {
    ...filters,
    facility_id: effectiveFacilityId ?? undefined,
  };

  return useQuery({
    // `enabled: false` defers the fetch (e.g. a closed modal that shouldn't
    // pull a facility-wide incident list until actually opened).
    enabled: options?.enabled ?? true,
    queryKey: ["incidents", effectiveFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (effectiveFilters.facility_id)
        params.append("facility_id", effectiveFilters.facility_id);
      // Status may be a single value or several (multi-select) — append each.
      if (Array.isArray(effectiveFilters.status))
        effectiveFilters.status.forEach((s) => params.append("status", s));
      else if (effectiveFilters.status)
        params.append("status", effectiveFilters.status);
      if (effectiveFilters.severity)
        params.append("severity", effectiveFilters.severity);
      if (effectiveFilters.source)
        params.append("source", effectiveFilters.source);
      if (effectiveFilters.search)
        params.append("search", effectiveFilters.search);
      if (effectiveFilters.limit)
        params.append("limit", String(effectiveFilters.limit));
      if (effectiveFilters.page && effectiveFilters.limit)
        params.append(
          "offset",
          String((effectiveFilters.page - 1) * effectiveFilters.limit),
        );

      const { data } = await api.get(`/incidents?${params.toString()}`);
      return data.data as IncidentsPage;
    },
  });
}

/** Lightweight incident counts (no PHI rows) for dashboard/banner surfacing. */
export interface IncidentSummary {
  /**
   * ALL incidents still at the un-triaged `reported` status, whoever filed them
   * (US-167 / F-061 — the dashboard tile reports this).
   */
  untriaged: number;
  /**
   * The automation-filed subset (reported_by = SYSTEM_USER_ID). Still used by
   * the incidents-page banner, which is correctly labelled "Review system
   * incidents" and deep-links to exactly that set.
   */
  system_untriaged: number;
}

/**
 * Counts of system-generated incidents that nobody has triaged yet — backs the
 * "System Incidents" dashboard tile and the incidents-page banner. Falls back to
 * the current facility context like {@link useIncidents}; polls so a freshly
 * auto-generated incident surfaces without a manual refresh.
 */
export function useIncidentSummary(facilityId?: string) {
  const { currentFacilityId } = useFacilityContext();
  const effectiveFacilityId = facilityId ?? currentFacilityId ?? undefined;

  return useQuery({
    queryKey: ["incidents", "summary", effectiveFacilityId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (effectiveFacilityId)
        params.append("facility_id", effectiveFacilityId);
      const { data } = await api.get(`/incidents/summary?${params.toString()}`);
      return data.data as IncidentSummary;
    },
    refetchInterval: 60000,
  });
}

/**
 * Fetch a single incident by ID
 */
export function useIncident(incidentId: string) {
  return useQuery({
    queryKey: ["incidents", incidentId],
    queryFn: async () => {
      const { data } = await api.get(`/incidents/${incidentId}`);
      return data.data as Incident;
    },
    enabled: !!incidentId,
  });
}

/**
 * Create a new incident
 */
export function useCreateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (incident: IncidentInsert) => {
      const { data } = await api.post("/incidents", incident);
      return data.data as Incident;
    },
    // US-081: IncidentForm owns error UX via handleFormErrors — inline 400
    // field mapping + its own generic toast (US-080). Global handler stays silent.
    meta: FORM_MUTATION_META,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      invalidateResidentTimeline(queryClient);
      notifications.show({
        title: "Success",
        message: "Incident reported successfully",
        color: "green",
      });
    },
  });
}

/**
 * Update an existing incident
 */
export function useUpdateIncident(incidentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (incident: IncidentUpdate) => {
      const { data } = await api.put(`/incidents/${incidentId}`, incident);
      return data.data as Incident;
    },
    // US-081: IncidentForm owns error UX via handleFormErrors — inline 400
    // field mapping + its own generic toast (US-080). Global handler stays silent.
    meta: FORM_MUTATION_META,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["incidents", incidentId] });
      invalidateResidentTimeline(queryClient);
      notifications.show({
        title: "Success",
        message: "Incident updated successfully",
        color: "green",
      });
    },
  });
}

/**
 * US-090 (CAPA) — resolve/close an incident with the corrective-action record.
 * Distinct from {@link useUpdateIncident} because the close payload carries the
 * CAPA fields (`closure_reason`, `preventive_action`) not present on
 * `IncidentUpdate`. The backend rejects (400) unless ≥1 corrective action exists
 * AND a closure reason is provided.
 */
export interface ResolveIncidentPayload {
  status: "resolved";
  closure_reason: string;
  preventive_action?: string | null;
}

export function useResolveIncident(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ResolveIncidentPayload) => {
      const { data } = await api.put(`/incidents/${incidentId}`, payload);
      return data.data as Incident;
    },
    meta: { errorFallback: "Failed to resolve incident" },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["incidents", incidentId] });
      invalidateResidentTimeline(queryClient);
      notifications.show({
        title: "Incident resolved",
        message:
          "The corrective-action record was saved and the incident closed.",
        color: "green",
      });
    },
  });
}

/**
 * Change an incident's status by id (SCRUM-113).
 *
 * Unlike {@link useUpdateIncident}, this isn't bound to a single incident id, so
 * it can be called once at a list/table level and invoked per row
 * (`mutate({ id, status })`) — React hooks can't be created inside a row loop.
 */
export function useUpdateIncidentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data } = await api.put(`/incidents/${id}`, { status });
      return data.data as Incident;
    },
    // US-081: global handler shows server message or this fallback.
    meta: { errorFallback: "Failed to update status" },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["incidents", id] });
      invalidateResidentTimeline(queryClient);
      notifications.show({
        title: "Status updated",
        message: "Incident status changed.",
        color: "green",
      });
    },
  });
}

/**
 * Archive an incident (org admin / facility manager — incident:delete).
 * SCRUM-165: the DELETE route is a soft-delete (US-112 Phase 3 tombstone) —
 * this hook name matches that behavior instead of implying a hard delete.
 */
export function useArchiveIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (incidentId: string) => {
      const { data } = await api.delete(`/incidents/${incidentId}`);
      return data.data;
    },
    // US-081: global handler shows server message or this fallback.
    meta: { errorFallback: "Failed to archive incident" },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      invalidateResidentTimeline(queryClient);
      notifications.show({
        title: "Success",
        message: "Incident archived successfully",
        color: "green",
      });
    },
  });
}
